import React, { useEffect, useState } from 'react';
import { useAuth, api } from '@/context/AuthContext';
import { Navigate, useLocation } from 'react-router-dom';

export function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [needsValues, setNeedsValues] = useState(false);

  useEffect(() => {
    if (!user) {
      setOnboardingChecked(true);
      return;
    }
    // Skip check on onboarding and values pages
    if (location.pathname === '/onboarding' || location.pathname === '/values-agreement') {
      setOnboardingChecked(true);
      setNeedsOnboarding(false);
      setNeedsValues(false);
      return;
    }
    // If user object has onboarded flag already set true, skip onboarding check
    if (user.onboarded) {
      setNeedsOnboarding(false);
      // Check values acceptance (now also version-aware + reaffirmation-aware)
      api.get('/values-agreement/check').then(res => {
        const { needs_acceptance, needs_reaffirmation, values_accepted } = res.data || {};
        const block = needs_acceptance || needs_reaffirmation || !values_accepted;
        setNeedsValues(Boolean(block));
        setOnboardingChecked(true);
      }).catch(() => {
        setOnboardingChecked(true);
      });
      return;
    }
    // Check both onboarding and values
    Promise.all([
      api.get('/onboarding'),
      api.get('/values-agreement/check'),
    ]).then(([onbRes, valRes]) => {
      setNeedsOnboarding(!onbRes.data.onboarded);
      const v = valRes.data || {};
      const block = v.needs_acceptance || v.needs_reaffirmation || !v.values_accepted;
      setNeedsValues(Boolean(block));
      setOnboardingChecked(true);
    }).catch(() => {
      setOnboardingChecked(true);
    });
  }, [user, location.pathname]);

  if (loading || !onboardingChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="animate-pulse">
          <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }} />
        </div>
      </div>
    );
  }

  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth?next=${next}`} replace />;
  }

  if (needsOnboarding && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  if (needsValues && location.pathname !== '/values-agreement') {
    return <Navigate to="/values-agreement" replace />;
  }

  return children;
}
